/**
 * The heartbeat of idle games.
 *
 *   simulate(state, dtMs, { capMs, rng }) → state'
 *
 * Pure function. No DOM, no React, no fetch, no clock. The same
 * (state, dt, rng-sequence) tuple always produces the same state'.
 *
 * Phase 2 contract (enforced by offline/simulate.test.ts):
 *   - idempotent: simulate(s, 0) === s
 *   - only COMPLETE action intervals produce XP/damage/drops;
 *     partial progress is banked in state.actionProgressMs
 *   - capped: online ≤ 5s per call; offline ≤ 24h
 *   - every random roll draws from the injected rng (Math.random is only
 *     the production default — never called when an rng is supplied)
 */

import type { ItemId, PlayerState, ActiveSkill, MonsterId, SkillId } from '../state';
import { getSkill, SKILLS } from '../skills/spec';
import { xpPerTick, levelForXp } from '../skills/xp';
import { getMonster } from '../monsters/spec';
import { awardItem, type Containers } from '../economy/containers';
import { totalBonuses } from '../economy/equipment';
import { applyQuestKill, type QuestEvent } from '../quests/engine';
import {
  BOSS_MONSTER_ID,
  bossPhase,
  critQuote,
  CRIT_MULTIPLIER,
  isCritRound,
  PHASE2_BONUS_DAMAGE,
  PHASE3_INTERVAL_MULT,
  type BossPhase,
} from '../monsters/boss';

import { STARTER_LADLE_FLAG } from '../quests/tutorial';
import { rollDrop } from '../monsters/drops';

/** Everything the simulation can announce to the UI layer. */
export type GameEvent =
  | QuestEvent
  | { type: 'boss-crit'; round: number; quote: string }
  | { type: 'boss-phase'; phase: BossPhase }
  | { type: 'starter-grant' };

/** Cap per call so a single tick can't simulate the heat death of the universe. */
export const ONLINE_SIM_CAP_MS = 5_000;
export const OFFLINE_SIM_CAP_MS = 24 * 60 * 60 * 1_000;

/** Flat per-tick chance for each eligible skill drop entry. */
export const SKILL_DROP_CHANCE = 0.05;

export type Rng = () => number;

export type SimulateOptions = {
  /** Max dt to simulate. Default ONLINE_SIM_CAP_MS; pass OFFLINE_SIM_CAP_MS for catch-up. */
  capMs?: number;
  /** Deterministic RNG; defaults to Math.random in production, seeded in tests. */
  rng?: Rng;
};

/** Interval of one complete action tick, or null when idle. */
export function actionIntervalMs(active: ActiveSkill): number | null {
  switch (active.kind) {
    case 'idle':
      return null;
    case 'train':
      return getSkill(active.skillId).baseIntervalSec * 1_000;
    case 'combat':
      return getMonster(active.monsterId).attackIntervalSec * 1_000;
  }
}

/**
 * State-aware interval: the Parakeet's phase 3 (≤20% HP) halves the combat
 * interval, so the interval must be re-read from CURRENT hp each loop pass.
 */
export function currentActionIntervalMs(state: PlayerState): number | null {
  const base = actionIntervalMs(state.active);
  if (base === null) return null;
  if (state.active.kind === 'combat' && state.active.monsterId === BOSS_MONSTER_ID) {
    const monster = getMonster(BOSS_MONSTER_ID);
    const hp = state.hp.monster ?? monster.hp;
    if (bossPhase(hp, monster.hp) === 3) return base * PHASE3_INTERVAL_MULT;
  }
  return base;
}

function containersOf(state: PlayerState): Containers {
  return { bank: state.bank, inventory: state.inventory };
}

/** Callback for rolled loot that fit nowhere; callers must surface it. */
type LoseFn = (itemId: ItemId, qty: number) => void;

/** One complete skill training tick: XP plus rng-driven drop rolls. */
function tickTrain(
  state: PlayerState,
  skillId: SkillId,
  rng: Rng,
  lose: LoseFn,
  emit: (event: GameEvent) => void,
): PlayerState {
  const spec = getSkill(skillId);
  const progress = state.skills[skillId];
  const xpGain = xpPerTick(spec.baseXpPerTick, progress.level);
  const newXp = progress.xp + xpGain;
  const newLevel = levelForXp(newXp);

  let containers = containersOf(state);
  for (const drop of spec.drops) {
    if (newLevel >= drop.minLevel && rng() < SKILL_DROP_CHANCE) {
      const awarded = awardItem(containers, drop.itemId, 1);
      containers = awarded.containers;
      if (awarded.lost > 0) lose(drop.itemId, awarded.lost);
    }
  }

  // Tutorial beat 3: the first completed Codex Whispering tick banks exactly
  // one Polished Ladle. Flag-guarded because it is not inferable from state
  // (the ladle can also arrive as a normal skill drop).
  let flags = state.flags;
  if (skillId === 'codex_whispering' && !flags.includes(STARTER_LADLE_FLAG)) {
    const awarded = awardItem(containers, 'polished_ladle', 1);
    if (awarded.lost === 0) {
      containers = awarded.containers;
      flags = [...flags, STARTER_LADLE_FLAG];
      emit({ type: 'starter-grant' });
    }
  }

  return {
    ...state,
    skills: { ...state.skills, [skillId]: { level: newLevel, xp: newXp } },
    bank: containers.bank,
    inventory: containers.inventory,
    flags,
  };
}

/** Callback for simulation events (quest completions etc.). */
type EmitFn = (event: GameEvent) => void;

/** One complete combat round: player strikes, then the monster strikes back. */
function tickCombat(
  state: PlayerState,
  monsterId: MonsterId,
  rng: Rng,
  lose: LoseFn,
  emit: EmitFn,
): PlayerState {
  const monster = getMonster(monsterId);
  const bonuses = totalBonuses(state.equipment);
  const maxPlayerHp = 100 + bonuses.hp;
  const isBoss = monsterId === BOSS_MONSTER_ID;
  const round = state.combatRound + 1;
  const crit = isBoss && isCritRound(round);
  const playerDamage =
    (monster.damageToMonster + bonuses.attack) * (crit ? CRIT_MULTIPLIER : 1);
  const prevMonsterHp = state.hp.monster ?? monster.hp;
  const monsterHp = prevMonsterHp - playerDamage;
  if (crit) emit({ type: 'boss-crit', round, quote: critQuote(round) });

  if (monsterHp <= 0) {
    // Kill: roll ONE weighted drop (Phase 4 contract — `weight` is no
    // longer decorative). The roller consumes exactly two rng() draws
    // (winner pick + qty sample) when it awards, zero when the monster
    // declares no rows — preserving the deterministic replay sequence.
    let containers = containersOf(state);
    const drop = rollDrop(monster.drops, rng);
    if (drop) {
      const awarded = awardItem(containers, drop.itemId, drop.qty);
      containers = awarded.containers;
      if (awarded.lost > 0) lose(drop.itemId, awarded.lost);
    }
    const killed: PlayerState = {
      ...state,
      bank: containers.bank,
      inventory: containers.inventory,
      active: { kind: 'idle' },
      combatRound: 0,
      hp: { player: maxPlayerHp, monster: null },
    };
    // The kill credits quests HERE — the one seam shared by the online tick
    // and offline catch-up, so rewards can never double-apply per path.
    const credited = applyQuestKill(killed, monsterId, lose);
    for (const event of credited.events) emit(event);
    return credited.state;
  }

  // Boss phase announcements fire exactly once, on the crossing strike.
  if (isBoss) {
    const prevPhase = bossPhase(prevMonsterHp, monster.hp);
    const newPhase = bossPhase(monsterHp, monster.hp);
    if (newPhase > prevPhase) emit({ type: 'boss-phase', phase: newPhase });
  }

  // Phase 2+ Parakeet chip damage: +4 AFTER normal defence treatment.
  const phaseBonus =
    isBoss && bossPhase(monsterHp, monster.hp) >= 2 ? PHASE2_BONUS_DAMAGE : 0;
  const playerHp =
    state.hp.player - (Math.max(1, monster.damageToPlayer - bonuses.defence) + phaseBonus);
  if (playerHp <= 0) {
    // Death: no drops, encounter ends.
    return {
      ...state,
      active: { kind: 'idle' },
      combatRound: 0,
      hp: { player: maxPlayerHp, monster: null },
    };
  }

  return { ...state, combatRound: round, hp: { player: playerHp, monster: monsterHp } };
}

export type LostItem = { itemId: ItemId; qty: number };

export type SimulateDetail = {
  state: PlayerState;
  ticks: number;
  /** Rolled loot that fit in neither container — never silently discarded. */
  lostItems: ReadonlyArray<LostItem>;
  /** Transition events (quest completions, unlocks) — each fires exactly once. */
  events: ReadonlyArray<GameEvent>;
};

/** The heartbeat, with tick + lost-loot accounting for offline summaries. */
export function simulateDetailed(
  state: PlayerState,
  dtMs: number,
  options: SimulateOptions = {},
): SimulateDetail {
  if (dtMs <= 0) return { state, ticks: 0, lostItems: [], events: [] };
  if (state.active.kind === 'idle') return { state, ticks: 0, lostItems: [], events: [] };

  const cap = options.capMs ?? ONLINE_SIM_CAP_MS;
  const dt = Math.min(dtMs, cap);
  const rng = options.rng ?? Math.random;
  const lost = new Map<ItemId, number>();
  const lose: LoseFn = (itemId, qty) => lost.set(itemId, (lost.get(itemId) ?? 0) + qty);
  const events: GameEvent[] = [];
  const emit: EmitFn = (event) => events.push(event);

  let s = state;
  let budget = state.actionProgressMs + dt;
  let ticks = 0;
  let interval = currentActionIntervalMs(s);

  while (interval !== null && budget >= interval) {
    budget -= interval;
    const active = s.active;
    if (active.kind === 'train') s = tickTrain(s, active.skillId, rng, lose, emit);
    else if (active.kind === 'combat') s = tickCombat(s, active.monsterId, rng, lose, emit);
    else break;
    ticks += 1;
    if (s.active.kind === 'idle') {
      // Action ended (kill or death): the rest of the window idles away.
      budget = 0;
      break;
    }
    interval = currentActionIntervalMs(s);
  }

  return {
    state: { ...s, actionProgressMs: budget },
    ticks,
    lostItems: [...lost.entries()].map(([itemId, qty]) => ({ itemId, qty })),
    events,
  };
}

export function simulate(state: PlayerState, dtMs: number, options: SimulateOptions = {}): PlayerState {
  return simulateDetailed(state, dtMs, options).state;
}

/** Convenience: list of skill ids (typed union). */
export const ALL_SKILL_IDS: ReadonlyArray<SkillId> = SKILLS.map((skill) => skill.id);
